import _ from 'lodash';
import { useEffect, useState } from 'react';
import { View } from 'react-native';

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import {
  AppInsightsErrorBoundary,
  ReactPlugin
} from '@microsoft/applicationinsights-react-js';

import { Button, COLORS, Text } from './src/components/Basics';
import { Container } from './src/components/Container';
import { LorentzLoginScreen, LorentzOverlay } from './src/components/Lorentz';
import { SettingsScreen, PhoneScreen } from './src/screens';
import { ContextProvider, useStore } from './src/store/Context';
import { parseEmbedSearchParams } from './src/utils/supervisorEmbed';
import LorentzService from './src/services/LorentzService';
import { registerPwa } from './src/utils/pwa';

const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    connectionString:
      'InstrumentationKey=89ba4ad2-47ef-41e8-a151-4b3488132c63;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/',
    extensions: [reactPlugin]
  }
});
const insightsHost = typeof window !== 'undefined' ? window.location.hostname : '';
const skipInsights = /^(localhost|127\.0\.0\.1|10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(insightsHost);
if (!skipInsights) {
  appInsights.loadAppInsights();
  appInsights.trackPageView();
}

function AppContent() {
  const store = useStore();
  const { settingsUri, toggleShowSettings, server, showSettings, lorentz = {}, lorentzLogout } =
    store;
  const [showLorentzLogin, setShowLorentzLogin] = useState(false);
  const [embedDefaults, setEmbedDefaults] = useState(() => {
    if (typeof window === 'undefined') return {};
    return parseEmbedSearchParams(window.location.search);
  });

  const embedMode = embedDefaults.embed || lorentz.supervisorEmbed;

  const needsLorentzLogin =
    lorentz.enabled && !lorentz.authenticated && !server;
  const [configBoot, setConfigBoot] = useState(true);

  useEffect(() => {
    let cancel = false;
    let gen = 0;
    let chain = Promise.resolve();
    const boot = () => {
      if (cancel) return;
      const my = ++gen;
      chain = chain.catch(() => {}).then(async () => {
        if (cancel || my !== gen) return;
        const state = useStore.getState();
        const params = new URLSearchParams(window.location.search);
        const pageConfig = params.get('config') || params.get('configUrl') || '';
        if (!pageConfig && state.settingsUri) {
          setConfigBoot(false);
          return;
        }
        const url = pageConfig || '/cfgLorentz008.json';
        try {
          await state.login({ settingsUri: url, nickname: '', password: '' });
        } catch (err) {
          if (!cancel && my === gen) state.toggleShowSettings(true, 'connection');
        } finally {
          if (!cancel && my === gen) setConfigBoot(false);
        }
      });
    };

    const persist = useStore.persist;
    const unsub = persist?.onFinishHydration?.(() => boot());
    if (!persist || persist.hasHydrated?.()) boot();
    const timer = setTimeout(boot, 800);
    return () => {
      cancel = true;
      clearTimeout(timer);
      unsub?.();
    };
  }, []);

  useEffect(() => {
    if (configBoot) return;
    const mustSettings = !settingsUri && !needsLorentzLogin;
    if (mustSettings) toggleShowSettings(true, 'connection');
    else if (!needsLorentzLogin) toggleShowSettings(false);
  }, [settingsUri, needsLorentzLogin, configBoot]);

  useEffect(() => {
    setShowLorentzLogin(needsLorentzLogin);
  }, [needsLorentzLogin]);

  useEffect(() => {
    registerPwa();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewId = params.get('view');
    if (viewId) {
      store.applyViewLayout?.(viewId).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onAuthExpired = () => {
      store.lorentzLogout?.();
      setShowLorentzLogin(true);
    };

    document.addEventListener('lorentz:auth:expired', onAuthExpired);
    return () =>
      document.removeEventListener('lorentz:auth:expired', onAuthExpired);
  }, []);

  useEffect(() => {
    const onSupervisorInit = ev => {
      const data = ev.detail || {};
      setEmbedDefaults(prev => ({
        ...prev,
        embed: true,
        configUrl: data.configUrl || data.config || prev.configUrl,
        username: data.username || prev.username,
        groupName: data.groupName || prev.groupName
      }));
      store.setSettings({
        lorentz: {
          ...store.lorentz,
          extension: data.extension ? String(data.extension) : store.lorentz?.extension,
          supervisorEmbed: true,
          supervisorPanels: data.panels || store.lorentz?.supervisorPanels
        }
      });
    };

    const onMessage = ev => {
      const data = ev.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'lorentz:supervisor:init') {
        onSupervisorInit({ detail: data });
        const configUrl = data.configUrl || data.config;
        if (configUrl) {
          useStore
            .getState()
            .login({ settingsUri: configUrl, nickname: '', password: '' })
            .catch(() => {});
        }
        if (data.viewLayoutId) {
          store.applyViewLayout?.(data.viewLayoutId, {
            lorentz: { supervisorPanels: data.panels }
          });
        }
      }
      if (data.type === 'lorentz:supervisor:answer' || data.type === 'lorentz:supervisor:reject') {
        document.dispatchEvent(new CustomEvent(data.type));
      }
      if (data.type === 'lorentz:supervisor:dial' && data.phone) {
        LorentzService.handleInboundEvent('lorentz:supervisor:dial', data);
      }
      if (data.type === 'lorentz:transfer' && (data.destination || data.phone)) {
        LorentzService.handleInboundEvent('lorentz:transfer', data);
      }
      if (data.type === 'lorentz:supervisor:logout') {
        lorentzLogout?.();
      }
    };

    document.addEventListener('lorentz:supervisor:init', onSupervisorInit);
    window.addEventListener('message', onMessage);

    try {
      window.parent.postMessage({ type: 'lorentz:supervisor:ready' }, '*');
    } catch {
      /* standalone */
    }

    return () => {
      document.removeEventListener('lorentz:supervisor:init', onSupervisorInit);
      window.removeEventListener('message', onMessage);
    };
  }, []);

  const phoneReady = settingsUri && !needsLorentzLogin;

  return (
    <Container>
      {phoneReady && !server && <PhoneScreen />}
      <LorentzOverlay />
      <LorentzLoginScreen
        visible={showLorentzLogin}
        embedMode={embedMode}
        embedDefaults={embedDefaults}
        onSuccess={() => setShowLorentzLogin(false)}
      />
      <SettingsScreen closeable={!server} visible={showSettings || server} />
    </Container>
  );
}

export default function App() {
  return (
    <AppInsightsErrorBoundary
      onError={() => (
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <Button
            style={{ padding: 10 }}
            color={COLORS.primary}
            onClick={() => window?.location.reload()}
          >
            <Text style={{ color: 'white' }}>
              Something went wrong. Reload app.
            </Text>
          </Button>
        </View>
      )}
      appInsights={reactPlugin}
    >
      <ContextProvider>
        <AppContent />
      </ContextProvider>
    </AppInsightsErrorBoundary>
  );
}
